import { useEffect, useMemo, useState } from "react";
import { Box } from "@mui-wrapper";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { faroCoreApiClient } from "@api/faro-core-api-client";
import { useErrorHandler } from "@components/error-boundary/error-handling-context";
import { RichTextField } from "@components/common/rich-text-field";
import {
  Autocomplete,
  AutoCompleteOption,
  AutoCompleteSelectedOption,
  Button,
  Dialog,
  Text,
  useToast,
} from "@flat-ui";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { PlanEvents } from "@utils/track-event/track-event-list";

/** Bundle identifiers are lower case words separated by dashes, e.g. "basic-dashboard" */
const BUNDLE_IDENTIFIER_REGEX = /^[a-z0-9]+(-[a-z0-9]+)*$/;

interface Props {
  /** Optional callback invoked after a bundle was created successfully */
  onBundleCreated?: () => void;
}

/** Button and dialog to create a new feature bundle */
export function CreateBundleDialog({
  onBundleCreated,
}: Props): React.JSX.Element {
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [isFetchingFeatures, setIsFetchingFeatures] = useState<boolean>(false);
  const [allFeatures, setAllFeatures] = useState<FaroCoreApiTypes.AdmFeature[]>(
    []
  );
  const [identifier, setIdentifier] = useState<string>("");
  const [name, setName] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [selectedFeatureIds, setSelectedFeatureIds] = useState<string[]>([]);

  const { trackEvent } = useTrackEvent();
  const { handleErrorWithToast } = useErrorHandler();
  const { openToast } = useToast();

  const isIdentifierValid = BUNDLE_IDENTIFIER_REGEX.test(identifier);
  const isNameValid = name.trim().length > 0;
  const isFeaturesValid = selectedFeatureIds.length > 0;

  // Fetch all features every time the dialog is opened to include newly created ones
  useEffect(() => {
    if (!isDialogOpen) {
      return;
    }

    async function fetchFeatures(): Promise<void> {
      setIsFetchingFeatures(true);
      try {
        setAllFeatures(await faroCoreApiClient.admin.getAllFeatures());
      } catch (error) {
        handleErrorWithToast({
          id: `fetch-features-${Date.now().toString()}`,
          title: "Could not fetch features",
          error,
        });
      } finally {
        setIsFetchingFeatures(false);
      }
    }

    void fetchFeatures();
  }, [isDialogOpen, handleErrorWithToast]);

  const featureOptions = useMemo<AutoCompleteOption[]>(
    () =>
      [...allFeatures]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((feature) => ({
          title: feature.name,
          subtitle: feature.identifier,
          value: feature.id,
        })),
    [allFeatures]
  );

  const selectedFeatureOptions = useMemo<AutoCompleteOption[]>(
    () =>
      featureOptions.filter((option) =>
        selectedFeatureIds.includes(option.value)
      ),
    [featureOptions, selectedFeatureIds]
  );

  function handleFeaturesChange(options: AutoCompleteSelectedOption[]): void {
    // Only features from the list can be selected, free text is ignored
    setSelectedFeatureIds(
      options
        .filter((option): option is AutoCompleteOption => !isString(option))
        .map((option) => option.value)
    );
  }

  function handleDialogClose(): void {
    setIsDialogOpen(false);

    // Reset form to initial state
    setIdentifier("");
    setName("");
    setDescription("");
    setSelectedFeatureIds([]);
  }

  async function onCreateBundle(): Promise<void> {
    trackEvent({
      name: PlanEvents.createBundle,
      props: { identifier, featuresCount: selectedFeatureIds.length },
    });

    setIsCreating(true);
    try {
      await faroCoreApiClient.admin.createBundle({
        identifier,
        name: name.trim(),
        description: description.trim(),
        features: selectedFeatureIds,
      });
      openToast({
        title: `Bundle "${name.trim()}" created successfully`,
      });
      handleDialogClose();
      onBundleCreated?.();
    } catch (error) {
      handleErrorWithToast({
        id: `create-bundle-${Date.now().toString()}`,
        title: "Could not create bundle",
        error,
      });
    } finally {
      setIsCreating(false);
    }
  }

  return (
    <>
      <Button onClick={() => setIsDialogOpen(true)}>Create bundle</Button>

      <Dialog
        open={isDialogOpen}
        title="Create bundle"
        onCancel={handleDialogClose}
        onConfirm={onCreateBundle}
        showSpinner={isCreating}
        isConfirmDisabled={
          isCreating || !isIdentifierValid || !isNameValid || !isFeaturesValid
        }
      >
        <RichTextField
          label="Identifier"
          text={identifier}
          onChange={setIdentifier}
          helpTooltip="A human-readable primary key that is consistent between all deployments. It can't be changed later"
          validation={
            identifier.length === 0 || isIdentifierValid
              ? { isValid: true }
              : {
                  isValid: false,
                  message:
                    "Use lower case letters and numbers separated by dashes, e.g. basic-dashboard",
                }
          }
        />

        <RichTextField
          label="Name"
          text={name}
          onChange={setName}
          helpTooltip="Human-readable name of the bundle"
        />

        <RichTextField
          label="Description"
          text={description}
          onChange={setDescription}
        />

        <Box sx={{ mt: 2 }}>
          <Text variant="labelM">Features</Text>
          <Autocomplete
            options={featureOptions}
            selectedOptions={selectedFeatureOptions}
            handleChange={handleFeaturesChange}
            isValid={() => false}
            hideIcon={true}
            placeHolder={
              isFetchingFeatures
                ? "Loading features..."
                : "Search and select features"
            }
          />
        </Box>
      </Dialog>
    </>
  );
}

/** @returns true if the selected autocomplete option is a free text value */
function isString(option: AutoCompleteSelectedOption): option is string {
  return typeof option === "string";
}
